import cx from 'classnames';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {IconColor, IconName, Icon, Tooltip} from 'components/UIKit';
import {useObservable} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {onlineSessionsStore} from 'services';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SCREEN_RECORDER;

type Props = {
    messageAppearance?: 'tooltip' | 'right-label';
    className?: string;
    successClassName?: string;
    errorClassName?: string;
};

export const ScreenRecorderStatus: React.FC<Props> = (
    {messageAppearance = 'tooltip', className, successClassName, errorClassName}: Props
) => {
    const {t} = useTranslation();
    const {screenRecorderState: state} = useObservable(onlineSessionsStore);

    const error = useMemo<string>(() => {
        // Don't change order. Otherwise isValidVersion will never show
        if (!state || !state?.version) {
            return t(T_KEYS.TOOLTIPS.SCREEN_RECORDER_OFFLINE);
        } else if (!state.isValidVersion) {
            return t(T_KEYS.TOOLTIPS.SCREEN_RECORDER_INVALID_VERSION);
        } else if (!state.isOnline) {
            return t(T_KEYS.TOOLTIPS.SCREEN_RECORDER_OFFLINE);
        }
    }, [state, t]);

    const message = useMemo(() => error ?? t(T_KEYS.TOOLTIPS.SCREEN_RECORDER_ONLINE), [error, t]);

    const iconProps = useMemo<{
        name: IconName,
        color?: IconColor,
        className: string
    }>(() => error ? ({name: 'record-disabled', className: 'tw-mr-2'}) :
        ({name: 'record', color: 'alert', className: 'tw-mr-2 tw-animate-pulse'}), [error]);

    if (messageAppearance === 'tooltip') {
        return (
            <Tooltip message={message} position='left'>
                <Icon size='lg' {...iconProps} />
            </Tooltip>
        );
    } else if (messageAppearance === 'right-label') {
        return (
            <div className={cx('tw-flex-center', className, error ? errorClassName : successClassName)}>
                <Icon size='lg' {...iconProps} />
                <div className='tw-ml-2'>
                    {message}
                </div>
            </div>
        );
    }
    return null;
};

